<template>
  <div :class="[`${$options.name}`, classList()]" :data-e-ga-id="uniqueId">
    <div v-if="label || $slots.tooltip" class="tw:flex tw:gap-1 tw:items-center">
      <div v-if="label" :class="['tw:body-1 ', $options.name + '__label']">
        {{ label }}
      </div>
      <!--  @slot This slot is for the tooltip component.  -->
      <slot name="tooltip" />
    </div>
    <date-picker
      :class="`${$options.name}__component-wrapper`"
      :clearable="false"
      :disabled="disabled"
      :disabled-date="disabledDate"
      :disabled-time="disabledTime"
      :format="type === 'time' ? 'HH:mm' : type === 'datetime' ? 'DD MMM YYYY HH:mm' : 'DD MMM YYYY'"
      :lang="lang"
      :placeholder="placeholder"
      :range="type === 'range'"
      :shortcuts="type === 'range' ? shortcuts : null"
      :type="type === 'range' ? 'date' : type"
      :value="internalValue"
      separator=" - "
      @change="handleChange"
    >
      <template #icon-calendar>
        <i :class="`${$options.name}-icon-wrapper`">
          <template v-if="invalid && !disabled">
            <font-awesome-icon
              :class="[`${$options.name}__icon`, `${$options.name}--invalid`]"
              icon="fa-light fa-circle-exclamation"
            />
          </template>
          <template v-else>
            <font-awesome-icon v-if="type === 'time'" :class="`${$options.name}__icon`" icon="fa-light fa-clock" />
            <font-awesome-icon v-else :class="`${$options.name}__icon`" icon="fa-light fa-calendar" />
          </template>
        </i>
      </template>
      <template v-if="$slots['custom-footer'] && type !== 'time'" #footer>
        <div style="text-align: left">
          <!--  @slot Use this slot for additional buttons in datepicker footer  -->
          <slot name="custom-footer" />
        </div>
      </template>
    </date-picker>
    <div v-if="!disabled && invalid && errorMsg" class="tw:caption tw:!text-red-500">
      {{ errorMsg }}
    </div>
    <div v-else-if="helper" class="tw:caption tw:!text-default-700">
      {{ helper }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from "vue";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { isValidDate } from "vue-app/utils/functions";
import {
  getDateInLocalTimezoneFromAccountTimezone,
  getDateInAccountTimezoneFromLocalTimezone,
} from "vue-app/datesDatepicker/datesDatepicker";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneDatepicker",
  components: {
    DatePicker,
  },
  props: {
    /**
     * The label of the datepicker.
     */
    label: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * The helper text to be displayed below the datepicker.
     */
    helper: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * The message to be displayed when the date is invalid.
     */
    errorMsg: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * The function that determines if the date is disabled. It should be a function that returns true or false and takes a Date object as an argument.
     *
     * See more: [DisabledDate](https://mengxiong10.github.io/vue2-datepicker/index.html#DisabledDateTime)
     */
    disabledDate: {
      type: Function,
      required: false,
      default: () => false,
    },
    /**
     * The function that determines if the time is disabled. It should be a function that returns true or false and takes a Date object as an argument.
     *
     * See more: [DisabledTime](https://mengxiong10.github.io/vue2-datepicker/index.html#DisabledDateTime)
     */
    disabledTime: {
      type: Function,
      required: false,
      default: () => false,
    },
    /**
     * The value of the datepicker.
     * It can be a Date object - for single date selection - or an array of two Date objects - for range selection.
     * The first Date object in the array is the start date and the second Date object is the end date.
     *
     * If you pass here a single `Date` object, you should set the `type` prop to different form `range`.
     * Otherwise, if you pass here an array of two `Date` objects, you should set the `type` prop to `range`.
     */
    value: {
      type: null as unknown as PropType<Date | [Date, Date] | null>,
      required: true,
    },
    /**
     * The type of the datepicker.
     */
    type: {
      type: String as PropType<"date" | "range" | "time" | "datetime">,
      required: false,
      default: "date",
    },
    /**
     * If the datepicker is disabled.
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The placeholder of the datepicker.
     */
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * If the datepicker is invalid.
     */
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The shortcuts for the range picker.
     *
     * See more: [Shortcuts](https://mengxiong10.github.io/vue2-datepicker/index.html#Shortcut)
     */
    shortcuts: {
      type: Array,
      required: false,
    },
    /**
     * If the datepicker should skip conversion from and to the account timezone.
     */
    ignoreTimezone: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: "",
    },
  },
  emits: [
    /**
     * Trigger at value change (date or time)
     */
    "input",
  ],
  setup(props, context) {
    const lang = {
      formatLocale: {
        firstDayOfWeek: 1,
      },
      monthFormat: "MMMM",
    };

    const internalValue = ref<Date | [Date, Date] | null>(props.value);

    watch(
      () => props.value,
      (newValue) => {
        if (internalIsValidDate(newValue)) {
          internalValue.value = setDate(newValue);
        }
      },
      { deep: true, immediate: true }
    );

    function setDate(date: Date | [Date, Date] | null) {
      if (!window.EDRONE_CONTEXT?.timezone || props.ignoreTimezone) {
        return date;
      }

      if (!date) {
        return null;
      }

      return date instanceof Array
        ? (date.map((date) => getDateInLocalTimezoneFromAccountTimezone(date)) as [Date, Date])
        : getDateInLocalTimezoneFromAccountTimezone(date);
    }

    function internalIsValidDate(date: Date | [Date, Date] | null) {
      return date instanceof Array ? date.every(isValidDate) : isValidDate(date);
    }

    /**
     * Expected type is: `value Date | [Date, Date] | null` but vue-datepicker-next is not typed well
     */
    function handleChange(value: unknown) {
      gtagUniversalSend({ component_id: props.uniqueId });
      // clearable is set to false, so we do not emit null value
      if (value && props.type !== "range" && value instanceof Date) {
        context.emit(
          "input",
          !window.EDRONE_CONTEXT?.timezone || props.ignoreTimezone
            ? value
            : getDateInAccountTimezoneFromLocalTimezone(value)
        );
        return;
      }
      if (Array.isArray(value) && props.type === "range") {
        context.emit(
          "input",
          !window.EDRONE_CONTEXT?.timezone || props.ignoreTimezone
            ? value
            : value.map((value) => getDateInAccountTimezoneFromLocalTimezone(value))
        );
        return;
      }
    }

    function classList() {
      return {
        "EdroneDatepicker--invalid": props.invalid,
        "EdroneDatepicker--disabled": props.disabled,
      };
    }

    return {
      internalValue,
      handleChange,
      lang,
      classList,
    };
  },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.mx-datepicker-popup {
  z-index: 41001;
  border-radius: 0.5rem !important;
  box-shadow: 0.25rem 0.25rem 1.5rem 0rem rgba(0, 0, 0, 0.22) !important;
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
}

.mx-datepicker-popup {
  @apply tw:border tw:bg-white tw:border-default-200;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    overflow: auto;
    margin: 0 !important;
  }
}

.mx-datepicker-popup .mx-time,
.mx-datepicker-popup .mx-date-time {
  border-radius: 0.5rem;
  width: fit-content;
  height: fit-content;
}

.mx-datepicker-popup .mx-time-column .mx-time-list {
  display: flex;
  padding: 0.75rem 1rem;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-time-column .mx-time-list {
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
  }
}

.mx-datepicker-popup .mx-time-column .mx-time-item {
  @apply tw:body-2;
  margin: 0;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.0175rem;
  display: flex;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0.625rem 0.5rem;
  border-radius: 1.25rem;
  transition: background-color 150ms ease-out, color 150ms ease-out;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-time-column .mx-time-item {
    width: 2rem;
    height: 2rem;
    padding: 0.375rem 0.25rem;
    border-radius: 1rem;
    font-size: 0.75rem;
  }
}

.mx-datepicker-popup .mx-time-column .mx-time-item:hover {
  @apply tw:bg-default-400;
}

.mx-datepicker-popup .mx-time-column .mx-time-item.active {
  @apply tw:text-white tw:bg-blue-500;
}

.mx-datepicker-popup .mx-time-column .mx-time-item.active:hover {
  @apply tw:bg-blue-600;
}

.mx-datepicker-popup .mx-time {
  height: stretch;
  height: -webkit-fill-available;
}

.mx-datepicker-popup .mx-calendar {
  padding: 1.25rem 1.5rem;
  width: fit-content;
  display: flex;
  flex-flow: column nowrap;
  gap: 1rem;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-calendar {
    padding: 0.75rem 1rem;
    gap: 0.5rem;
    width: 100%;
  }
}

@media (max-width: 30rem) {
  .mx-datepicker-popup .mx-calendar {
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
  }
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header {
  height: 2.5rem;
  display: flex;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-btn-icon-double-left,
.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-btn-icon-double-right {
  display: none;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-btn-icon-right {
  order: 4;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-btn-icon-left {
  order: 0;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-calendar-header-label {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  flex: 1;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-calendar-header-label .mx-btn-text {
  text-align: center;
  margin: 0;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: -0.02rem;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-calendar-header-label .mx-btn-text {
  @apply tw:body-1 tw:text-black;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-header .mx-calendar-header-label .mx-btn-text:hover {
  @apply tw:text-blue-600;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content {
  width: 100%;
  height: 100%;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table {
  height: auto;
  width: max-content;
  display: inline-block;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table td,
.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table th {
  text-align: center;
  @apply tw:body-2;
  margin: 0;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.0175rem;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table td {
  @apply tw:text-black;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table th {
  font-weight: 300;
  @apply tw:text-default-700;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .not-current-month {
  @apply tw:text-default-200;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .cell {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0.625rem 0.5rem;
  border-radius: 1.25rem;
  transition: background-color 150ms ease-out, color 150ms ease-out;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .cell {
    width: 2rem;
    height: 2rem;
    padding: 0.375rem 0.25rem;
    border-radius: 1rem;
    font-size: 0.75rem;
  }
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .cell:hover:not(.disabled) {
  @apply tw:bg-default-400;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .cell.active {
  @apply tw:bg-blue-500 tw:text-white;
}

.mx-datepicker-popup .mx-calendar .mx-calendar-content .mx-table .cell.active:hover {
  @apply tw:bg-blue-600;
}

.mx-datepicker-popup .mx-calendar-panel-year .mx-table,
.mx-datepicker-popup .mx-calendar-panel-month .mx-table {
  border-spacing: 0.25rem;
}

.mx-datepicker-popup .mx-calendar + .mx-time {
  width: 100%;
}

.mx-datepicker-popup .mx-calendar + .mx-time .mx-time-header {
  height: 2.5rem;
  display: flex;
  justify-content: center;
}

.mx-datepicker-popup .mx-calendar + .mx-time .mx-time-content {
  height: calc(100% - 2.5rem);
}

.mx-datepicker-popup .mx-datepicker-footer {
  box-sizing: content-box;
  height: 2.5rem;
  padding: 1rem;
}

.mx-datepicker-popup .mx-datepicker-sidebar {
  width: 8rem !important;
  padding: 1rem 0;
  gap: 0.5rem;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-datepicker-sidebar {
    width: 6.25rem !important;
    padding: 0.5rem 0;
    gap: 0.25rem;
  }
}

@media (max-width: 30rem) {
  .mx-datepicker-popup .mx-datepicker-sidebar {
    display: none !important;
  }
}

.mx-datepicker-popup .mx-datepicker-sidebar .mx-btn-shortcut {
  text-align: center;
  @apply tw:body-1;
  margin: 0;
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  letter-spacing: -0.02rem;
}

.mx-datepicker-popup .mx-datepicker-sidebar .mx-btn-shortcut {
  @apply tw:text-default-800;
}

.mx-datepicker-popup .mx-datepicker-sidebar .mx-btn-shortcut:hover {
  @apply tw:text-blue-600;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-datepicker-sidebar .mx-btn-shortcut {
    font-size: 0.875rem;
    line-height: 1.25rem;
    letter-spacing: -0.0175rem;
  }
}

.mx-datepicker-popup .mx-datepicker-sidebar + .mx-datepicker-content {
  margin-left: 8rem !important;
}

@media (max-width: 48rem) {
  .mx-datepicker-popup .mx-datepicker-sidebar + .mx-datepicker-content {
    margin-left: 6.25rem !important;
  }
}

@media (max-width: 30rem) {
  .mx-datepicker-popup .mx-datepicker-sidebar + .mx-datepicker-content {
    margin-left: 0 !important;
  }
}

.EdroneDatepicker {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  font-family: "Roboto", sans-serif;
}

.EdroneDatepicker__component-wrapper {
  width: 100% !important;
  max-width: 100% !important;
}

.EdroneDatepicker input {
  height: 2.5rem;
  padding: 0.625rem 2.625rem 0.625rem 0.75rem;
  border-radius: 0.25rem;
  box-shadow: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.0175rem;
  outline: none;
}

.EdroneDatepicker input {
  @apply tw:body-2 tw:bg-white tw:border tw:border-default-200 tw:text-black;
}

.EdroneDatepicker input:hover {
  @apply tw:border-default-400;
}

.EdroneDatepicker input:focus {
  @apply tw:border-blue-500;
}

.EdroneDatepicker input::placeholder {
  @apply tw:text-default-600;
}

.EdroneDatepicker input:disabled {
  @apply tw:text-default-800 tw:bg-default-100 tw:border-default-100;
}

.EdroneDatepicker input:disabled::placeholder {
  @apply tw:text-default-500;
}

.EdroneDatepicker .mx-icon-calendar,
.EdroneDatepicker .mx-icon-clear {
  right: 0.75rem;
  font-size: 1.25rem;
}

.EdroneDatepicker--invalid:not(.EdroneDatepicker--disabled) {
  @apply tw:text-red-500;
}

.EdroneDatepicker--invalid:not(.EdroneDatepicker--disabled) input {
  @apply tw:border-red-500;
}
</style>
